import { Injectable, inject } from '@angular/core';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { CountryDialogComponent } from '@warema/shared/dialogs';
import { SnackbarService } from '@warema/shared/helpers';
import { Country, CountryConfiguration, StorageConstants } from '@warema/shared/models';
import { TenantConfigurationService } from '@warema/shared/tenant-config';
import { SnackbarCloseType, SnackbarType } from '@warema/warema-ui';
import { PopupInputSignalValue, PopupService } from '@warema/warema-ui-angular';
import { firstValueFrom, timer } from 'rxjs';
import { debounce, first, switchMap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class CountryService {
    private readonly _snackbarService = inject(SnackbarService);
    private readonly _popupService = inject(PopupService);
    private readonly _translationService = inject(TranslateService);
    private readonly _tenantConfig = inject(TenantConfigurationService);

    public discardCountryPersistence(): void {
        localStorage.setItem(StorageConstants.COUNTRY_CODE, '');
    }

    public persistCountrySelection(countryConfig: CountryConfiguration, displayNotification: boolean): void {
        localStorage.setItem(StorageConstants.COUNTRY_CODE, countryConfig.ISOCode);

        let skipConfiguredCountryCode = false;
        const confirmedCountryCode = localStorage.getItem(StorageConstants.COUNTRY_CODE_CONFIRMED);
        if (countryConfig.ISOCode === confirmedCountryCode) {
            skipConfiguredCountryCode = true;
        } else {
            localStorage.removeItem(StorageConstants.COUNTRY_CODE_CONFIRMED);
        }

        if (!displayNotification || skipConfiguredCountryCode) {
            return;
        }

        // Skip snackbar if users browser-country matches the tenant-country
        const browserLocale = navigator.language;
        const browserCountry = browserLocale?.includes('-') ? browserLocale.split('-')?.[1] : null;
        if (browserCountry != null) {
            if (countryConfig.ISOCode === browserCountry) {
                return;
            }
        } else {
            // Use our most popular country as a fallback when no browser-country is set
            const languageCountryMap: Record<string, string> = {
                de: 'DE',
                fr: 'FR',
                nl: 'NL',
                en: 'GB',
            };

            const mappedCountry = languageCountryMap[browserLocale];
            if (mappedCountry != null && countryConfig.ISOCode === mappedCountry) {
                return;
            }
        }

        this._translationService
            .stream(`COUNTRY.${countryConfig.ISOCode}`)
            .pipe(
                first(),
                switchMap((country: string) =>
                    this._translationService.stream('COUNTRY_SELECTION.NOTIFICATION', { country }).pipe(
                        first(),
                        debounce(() => timer(1000)),
                    ),
                ),
            )
            .subscribe((notificationText: string) => {
                void this._snackbarService.showSnackbar({
                    type: SnackbarType.Warning,
                    text: notificationText,
                    actionText: this._translationService.instant('COUNTRY_SELECTION.CHANGE') as string,
                    duration: 100000,
                    actionCallback: () => {
                        const possibleCountries = Array.from(
                            this._tenantConfig.settings?.countryConfigMap.values() ?? [],
                        );
                        void this.openCountrySelectionDialog(possibleCountries, countryConfig.ISOCode);
                    },
                    closeCallback: closeType => {
                        if (closeType === SnackbarCloseType.Click) {
                            localStorage.setItem(StorageConstants.COUNTRY_CODE_CONFIRMED, countryConfig.ISOCode);
                        }
                    },
                });
            });
    }

    public async openCountrySelectionDialog(
        possibleCountries: Array<CountryConfiguration>,
        currentCountryCode: string,
    ): Promise<void> {
        const headline = this._translationService.instant('COUNTRY_SELECTION.CHANGE_YOUR_COUNTRY') as string;

        const countries = await this.translateAndSortCountries(possibleCountries.map(x => x.ISOCode));
        const currentCountry = countries.find(x => x.value === currentCountryCode);

        const popupInstance = this._popupService.open(
            CountryDialogComponent,
            {
                currentCountry: new PopupInputSignalValue<Country>(currentCountry),
                countries: new PopupInputSignalValue(countries),
            },
            {
                headline,
                size: 'small',
                centerAlignedDimensions: {
                    width: '100%',
                    maxWidth: '420px',
                },
            },
        );

        await popupInstance.popupHtmlElement;

        popupInstance.afterClosed$.pipe(first()).subscribe(result => {
            if (result) {
                localStorage.setItem(StorageConstants.COUNTRY_CODE, result.value);
                location.reload();
            }
        });
    }

    /** Translate country list, order alphabetical but DE, AT and CH should be first */
    public async translateAndSortCountries(countryCodes: Array<string>): Promise<Array<Country>> {
        const translationLabelPrefix = 'COUNTRY.';
        const countryListTranslateIds = countryCodes.map((c: string) => translationLabelPrefix + c);
        const translations = (await firstValueFrom(
            this._translationService.get(countryListTranslateIds),
        )) as TranslationObject;

        const countriesList = countryListTranslateIds.map((v, i): Country => {
            const translateProperty = countryListTranslateIds[i];
            return {
                label: translations[translateProperty] as string,
                value: v.replace(translationLabelPrefix, ''),
            };
        });

        const sortedCountries = [...countriesList].sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
        );

        return this._insertFirstCountriesAtBeginning(sortedCountries);
    }

    private _insertFirstCountriesAtBeginning(countries: Array<Country>): Array<Country> {
        const firstCountries = ['DE', 'AT', 'CH'];

        const result = countries.filter(r => !firstCountries.includes(r.value));

        [...firstCountries].reverse().forEach(country => {
            const firstCountry = countries.find(r => r.value === country);
            if (firstCountry) {
                result.unshift(firstCountry);
            }
        });

        return result;
    }
}
